/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package settings

import kotlinx.browser.localStorage

actual class SettingsManager {
    actual fun getSavingsTargetPercentage(): Double {
        val stored = localStorage.getItem(SETTINGS_SAVINGS_TARGET_KEY)
        return stored?.toDoubleOrNull() ?: DEFAULT_SAVINGS_TARGET_PERCENTAGE
    }

    actual fun setSavingsTargetPercentage(percentage: Double) {
        localStorage.setItem(SETTINGS_SAVINGS_TARGET_KEY, percentage.toString())
    }

    actual fun getChartVisibility(chartId: String): ChartVisibility {
        val stored = localStorage.getItem(chartId)
        return if (stored != null) {
            try {
                ChartVisibility.valueOf(stored)
            } catch (e: Exception) {
                ChartVisibility.COLLAPSED
            }
        } else {
            ChartVisibility.COLLAPSED
        }
    }

    actual fun setChartVisibility(chartId: String, visibility: ChartVisibility) {
        localStorage.setItem(chartId, visibility.name)
    }

    actual fun getChartSeriesVisibility(seriesId: String): Boolean {
        val key = "$CHART_SERIES_PREFIX$seriesId"
        val stored = localStorage.getItem(key)
        return stored?.toBooleanStrictOrNull() ?: true  // Default to visible
    }

    actual fun setChartSeriesVisibility(seriesId: String, visible: Boolean) {
        localStorage.setItem("$CHART_SERIES_PREFIX$seriesId", visible.toString())
    }

    actual fun getChartOrder(): List<String> {
        val stored = localStorage.getItem(CHART_ORDER_KEY)
        return if (!stored.isNullOrEmpty()) {
            stored.split(",").filter { it in ChartIds.REORDERABLE_CHARTS }
        } else {
            ChartIds.DEFAULT_CHART_ORDER
        }
    }

    actual fun setChartOrder(chartIds: List<String>) {
        localStorage.setItem(CHART_ORDER_KEY, chartIds.joinToString(","))
    }

    actual fun getLanguage(): String {
        return localStorage.getItem(SETTINGS_LANGUAGE_KEY) ?: DEFAULT_LANGUAGE
    }

    actual fun setLanguage(languageCode: String) {
        localStorage.setItem(SETTINGS_LANGUAGE_KEY, languageCode)
    }

    actual fun getThemePreference(): String {
        return localStorage.getItem(SETTINGS_THEME_KEY) ?: DEFAULT_THEME
    }

    actual fun setThemePreference(theme: String) {
        localStorage.setItem(SETTINGS_THEME_KEY, theme)
    }
}
